import { useMemo, useState } from 'react';
import { Box, Button, Card, EmptyState, Flex, Heading, HStack, Separator, Spinner, VStack } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { useNavigate } from '@tanstack/react-router';
import { FiSearch } from 'react-icons/fi';
import { LuArchive, LuArchiveRestore, LuChevronDown, LuChevronLeft, LuChevronUp } from 'react-icons/lu';
import type { PlanNodeOut } from '@/client';
import ArchiveEntryRow from '@/components/services/archive/ArchiveEntryRow';
import { byName, candidateEntry, contactEntry } from '@/components/services/model/contactArchive';
import {
	useArchivedContacts,
	useDiscardedCandidates,
	useRestoreArchiveEntry,
} from '@/hooks/service-providers/useContactArchive';
import { useHousePermissions } from '@/hooks/useHasPermission';
import type { CountedLensOption } from '@/components/common/CountedLensFilter';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import AssistantInput from '@/components/common/composer/AssistantInput';
import ComposerDock from '@/components/common/composer/ComposerDock';
import PageContent from '@/components/layout/PageContent';
import { canSendPlanAction } from '@/components/maintenance/model/planActions';
import { PlanActions } from '@/components/maintenance/model/planVocabulary';
import ProposalCard from '@/components/maintenance/review/ProposalCard';
import { BOTTOM_BAR_STRONG_FOCUS_SHADOW, BOTTOM_BAR_STRONG_SHADOW } from '@/components/ui/shadows';
import ArchiveViewPicker from '@/components/archive/ArchiveViewPicker';
import { MOBILE_BOTTOM_BAR_CLEARANCE } from '@/constants/layout';
import { useCurrentHouse } from '@/context/CurrentHouseContext';
import { useFeatureFlags } from '@/context/FeatureFlagsContext';
import { useUpdateStatus } from '@/context/UpdateStatusContext';
import { useDiscardedCards } from '@/hooks/maintenance/useDiscardedCards';
import { usePlanAction } from '@/hooks/maintenance/usePlanAction';
import { useVisibleDocuments } from '@/hooks/maintenance/useVisibleDocuments';
import { useDashboardAskHandoff } from '@/hooks/useDashboardAskHandoff';
import { ScheduleTabs } from '@/hooks/maintenance/useScheduleSearch';
import { propertyNavigateOptions, PropertyRouteTo } from '@/routes';

export const ArchiveViews = {
	DiscardedTasks: 'discarded-tasks',
	DeletedTasks: 'deleted-tasks',
	DeletedContacts: 'deleted-contacts',
	DiscardedContacts: 'discarded-contacts',
	DeletedRecords: 'deleted-records',
} as const;

export type ArchiveView = (typeof ArchiveViews)[keyof typeof ArchiveViews];

const VIEW_LABELS: Record<ArchiveView, string> = {
	[ArchiveViews.DiscardedTasks]: 'discarded tasks',
	[ArchiveViews.DeletedTasks]: 'deleted tasks',
	[ArchiveViews.DeletedContacts]: 'deleted contacts',
	[ArchiveViews.DiscardedContacts]: 'discarded candidates',
	[ArchiveViews.DeletedRecords]: 'deleted records',
};

/**
 * The views that read something today. The rest are the shape of a destination still being
 * built, and stay behind their flag.
 *
 * TODO(archive/permanent-delete): The contact views offer Delete permanently disabled. The API has
 * no permanent delete: `DELETE /contacts/{id}` is the soft delete that fills these lists, and
 * nothing purges a `removed` row or a rejected candidate. Enable the action once a purge route
 * exists, behind a confirm that says the corrections and history go with it.
 */
const DELETE_FOREVER_UNAVAILABLE = { unavailable: 'Permanent deletion is not available yet' } as const;

/** The pages an Archive link sits on. Back returns to the one the reader arrived from. */
export const ArchiveOrigins = { Schedule: 'schedule', Contacts: 'contacts' } as const;
export type ArchiveOrigin = (typeof ArchiveOrigins)[keyof typeof ArchiveOrigins];

interface ArchiveProps {
	initialView?: ArchiveView;
	origin?: ArchiveOrigin;
}

const ArchiveEmptyState = ({ view, hasSearch }: { view: ArchiveView; hasSearch: boolean }) => (
	<EmptyState.Root>
		<EmptyState.Content>
			<EmptyState.Indicator>
				<LuArchive size={48} />
			</EmptyState.Indicator>
			<VStack textAlign="center" gap={hasSearch ? 2 : 0}>
				<EmptyState.Title>
					{hasSearch ? `No matching ${VIEW_LABELS[view]}` : `No ${VIEW_LABELS[view]}`}
				</EmptyState.Title>
				{hasSearch ? <EmptyState.Description>Try a different search.</EmptyState.Description> : null}
			</VStack>
		</EmptyState.Content>
	</EmptyState.Root>
);

interface RestoreButtonProps {
	isLoading: boolean;
	isDisabled: boolean;
	onRestore: () => void;
}

/**
 * TODO(archive/discarded-task-recovery): Replace this temporary undo with final placement choices.
 * A discarded proposal has already been reviewed, and this archive card is where the person is
 * reconsidering it; sending it back to the review queue asks them to make the same decision twice.
 * Offer the review card's "Add to schedule" / "Keep unscheduled" choices here once the API can keep
 * a removed proposal with its placement in one atomic write. Do not chain `restore` then `keep` in
 * the client: if the second request fails, the proposal is silently stranded back in the queue.
 * Reserve plain Restore for deleted things that previously had an active destination.
 */
const RestoreButton = ({ isLoading, isDisabled, onRestore }: RestoreButtonProps) => (
	<OutlinedButton
		type="button"
		size="xs"
		h={8}
		w="auto"
		px={3}
		flexShrink={0}
		elevated
		loading={isLoading}
		disabled={isDisabled}
		onClick={(event) => {
			event.stopPropagation();
			onRestore();
		}}
	>
		<LuArchiveRestore />
		Restore
	</OutlinedButton>
);

/** The property's recoverable and removed things, reached from Schedule History. */
const Archive = ({ initialView = ArchiveViews.DiscardedTasks, origin }: ArchiveProps) => {
	const { house, houseId } = useCurrentHouse();
	const navigate = useNavigate();
	const { archiveDeletedViews } = useFeatureFlags();
	// The contact lists are gated on HOUSE_NETWORK_MANAGE by their endpoints, restore being the
	// only thing to do with them.
	const { canManageNetwork } = useHousePermissions();
	const archivedContacts = useArchivedContacts(houseId, canManageNetwork);
	const discardedCandidates = useDiscardedCandidates(houseId, canManageNetwork);
	const { restore: restoreEntry, isRestoring: isRestoringEntry } = useRestoreArchiveEntry(houseId);
	const { knowledgeBaseReadinessState } = useUpdateStatus();
	const discarded = useDiscardedCards(houseId);
	const { runAction, isPending: isRestoring } = usePlanAction(houseId);
	const { visibleDocumentIds } = useVisibleDocuments(houseId);
	const [view, setView] = useState<ArchiveView>(initialView);
	const [draftText, setDraftText] = useState('');
	const [search, setSearch] = useState('');
	const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
	const [restoringNodeId, setRestoringNodeId] = useState<string | null>(null);
	const handleAsk = useDashboardAskHandoff(house.public_id, draftText, setDraftText);
	// Each view from its own read: the merged list the contacts page draws has no place here.
	const deletedContacts = useMemo(
		() => (archivedContacts.data ?? []).map(contactEntry).sort(byName),
		[archivedContacts.data],
	);
	const discardedContacts = useMemo(
		() => (discardedCandidates.data?.candidates ?? []).map(candidateEntry).sort(byName),
		[discardedCandidates.data],
	);
	const deletedContactCount = archivedContacts.data === undefined ? undefined : deletedContacts.length;
	const discardedContactCount = discardedCandidates.data === undefined ? undefined : discardedContacts.length;

	const isContactView = view === ArchiveViews.DeletedContacts || view === ArchiveViews.DiscardedContacts;
	const isShellView = view === ArchiveViews.DeletedTasks || view === ArchiveViews.DeletedRecords;
	// A view the reader cannot see falls back to the one everyone can: the shells behind their flag,
	// the contact lists behind the permission their endpoints ask for.
	const activeView =
		(isShellView && !archiveDeletedViews) || (isContactView && !canManageNetwork)
			? ArchiveViews.DiscardedTasks
			: view;

	const options = useMemo<CountedLensOption<ArchiveView>[]>(() => {
		const discardedTasks: CountedLensOption<ArchiveView> = {
			value: ArchiveViews.DiscardedTasks,
			label: 'Discarded tasks',
			count: discarded.totalCount,
			tooltip: 'Bundle task suggestions that were discarded',
		};
		const contactViews: CountedLensOption<ArchiveView>[] = canManageNetwork
			? [
					{
						value: ArchiveViews.DeletedContacts,
						label: 'Deleted contacts',
						count: deletedContactCount,
						tooltip: 'Contacts removed after they were added',
					},
					{
						value: ArchiveViews.DiscardedContacts,
						label: 'Discarded candidates',
						count: discardedContactCount,
						tooltip: 'Contacts Bundle found in your records that were discarded in review',
					},
				]
			: [];
		if (!archiveDeletedViews) return [discardedTasks, ...contactViews];

		return [
			discardedTasks,
			{
				value: ArchiveViews.DeletedTasks,
				label: 'Deleted tasks',
				count: 0,
				tooltip: 'Tasks removed after they were added',
			},
			...contactViews,
			{
				value: ArchiveViews.DeletedRecords,
				label: 'Deleted records',
				count: 0,
				tooltip: 'Records removed from the property',
			},
		];
	}, [archiveDeletedViews, canManageNetwork, deletedContactCount, discarded.totalCount, discardedContactCount]);

	const filteredContacts = useMemo(() => {
		const entries =
			activeView === ArchiveViews.DeletedContacts
				? deletedContacts
				: activeView === ArchiveViews.DiscardedContacts
					? discardedContacts
					: [];
		const query = search.trim().toLocaleLowerCase();
		if (!query) return entries;
		return entries.filter((entry) =>
			`${entry.name} ${entry.detail ?? ''} ${entry.email ?? ''}`.toLocaleLowerCase().includes(query),
		);
	}, [activeView, deletedContacts, discardedContacts, search]);

	const filteredCards = useMemo(() => {
		if (activeView !== ArchiveViews.DiscardedTasks) return [];
		const query = search.trim().toLocaleLowerCase();
		if (!query) return discarded.cards;
		return discarded.cards.filter((card) =>
			`${card.title} ${card.description ?? ''}`.toLocaleLowerCase().includes(query),
		);
	}, [activeView, discarded.cards, search]);

	const restore = async (node: PlanNodeOut) => {
		setRestoringNodeId(node.node_id);
		try {
			const restored = await runAction(node, { action: PlanActions.Restore });
			if (restored && expandedNodeId === node.node_id) setExpandedNodeId(null);
		} finally {
			setRestoringNodeId(null);
		}
	};

	const isLoading =
		(activeView === ArchiveViews.DiscardedTasks && discarded.isLoading) ||
		(activeView === ArchiveViews.DeletedContacts && archivedContacts.isLoading) ||
		(activeView === ArchiveViews.DiscardedContacts && discardedCandidates.isLoading);
	const hasError =
		(activeView === ArchiveViews.DiscardedTasks && discarded.isError) ||
		(activeView === ArchiveViews.DeletedContacts && archivedContacts.isError) ||
		(activeView === ArchiveViews.DiscardedContacts && discardedCandidates.isError);
	const retry = () => {
		if (activeView === ArchiveViews.DiscardedTasks) void discarded.refetch();
		else if (activeView === ArchiveViews.DeletedContacts) void archivedContacts.refetch();
		else if (activeView === ArchiveViews.DiscardedContacts) void discardedCandidates.refetch();
	};
	const hasSearch = search.trim().length > 0;
	// Archive is a child destination of the page it was opened from, not a browser-history control:
	// a browser back can leave the app when this route was opened directly from an external page.
	const goBack = () =>
		void navigate(
			origin === ArchiveOrigins.Contacts
				? propertyNavigateOptions(house.public_id, PropertyRouteTo.serviceProviders, { tab: 'historical' })
				: propertyNavigateOptions(house.public_id, PropertyRouteTo.schedule, { tab: ScheduleTabs.History }),
		);

	return (
		<Flex h="full" minH={0} w="full" overflow="hidden" bg="surface.canvas">
			<Flex direction="column" flex="1" minH={0} overflowY="auto" overflowX="hidden">
				<PageContent pt={6}>
					<VStack w="full" align="stretch" gap={6}>
						<HStack gap={2}>
							<IconButton type="button" aria-label="Back" variant="ghost" size="sm" onClick={goBack}>
								<LuChevronLeft />
							</IconButton>
							<Heading size="2xl" color="brand.fg">
								Archive
							</Heading>
						</HStack>

						{options.length > 1 ? (
							<ArchiveViewPicker
								value={activeView}
								options={options}
								onChange={(next) => {
									setView(next);
									setSearch('');
									setDraftText('');
									setExpandedNodeId(null);
								}}
							/>
						) : null}

						{isLoading ? (
							<Flex justify="center" w="full" py={12}>
								<Spinner size="lg" />
							</Flex>
						) : hasError ? (
							<EmptyState.Root>
								<EmptyState.Content>
									<EmptyState.Indicator>
										<LuArchive size={48} />
									</EmptyState.Indicator>
									<EmptyState.Title>Archive could not be loaded</EmptyState.Title>
									<OutlinedButton size="sm" onClick={retry}>
										Try again
									</OutlinedButton>
								</EmptyState.Content>
							</EmptyState.Root>
						) : filteredContacts.length ? (
							<Card.Root layerStyle="card">
								<Card.Body p={0}>
									{filteredContacts.map((entry) => (
										<ArchiveEntryRow
											key={entry.key}
											entry={entry}
											houseId={houseId}
											propertyPublicId={house.public_id}
											isRestoring={isRestoringEntry(entry)}
											onRestore={() => restoreEntry(entry)}
											deleteForever={DELETE_FOREVER_UNAVAILABLE}
										/>
									))}
								</Card.Body>
							</Card.Root>
						) : filteredCards.length ||
						  (activeView === ArchiveViews.DiscardedTasks && discarded.hasNextPage) ? (
							// TODO(archive/discard-reason): Put the effective dismissal reason, optional note,
							// actor and date on the collapsed card once the discarded read returns them. Do not
							// recover that context with one timeline request per card; the archive endpoint should
							// expose the latest effective dismissal metadata with the row.
							<VStack align="stretch" gap={3}>
								{filteredCards.map((node) => {
									const isExpanded = expandedNodeId === node.node_id;
									const canRestore = canSendPlanAction(node, PlanActions.Restore);
									const restoreButton = canRestore ? (
										<RestoreButton
											isLoading={restoringNodeId === node.node_id}
											isDisabled={isRestoring && restoringNodeId !== node.node_id}
											onRestore={() => void restore(node)}
										/>
									) : null;

									return (
										<VStack key={node.node_id} align="stretch" gap={3} p={4} layerStyle="card">
											<ProposalCard
												node={node}
												onKeep={() => undefined}
												hideVerdicts
												flush
												showDetails={isExpanded}
												visibleDocumentIds={visibleDocumentIds}
											/>
											<Separator borderColor="border.default" />
											<HStack justify="space-between" gap={3}>
												<Button
													type="button"
													size="xs"
													variant="ghost"
													borderRadius="full"
													w="auto"
													aria-expanded={isExpanded}
													onClick={() => setExpandedNodeId(isExpanded ? null : node.node_id)}
												>
													{isExpanded ? <LuChevronUp /> : <LuChevronDown />}
													{isExpanded ? 'Show less' : 'Show details'}
												</Button>
												{restoreButton}
											</HStack>
										</VStack>
									);
								})}
								{discarded.hasNextPage ? (
									<OutlinedButton
										size="sm"
										alignSelf="center"
										loading={discarded.isFetchingNextPage}
										onClick={() => void discarded.fetchNextPage()}
									>
										Show more
									</OutlinedButton>
								) : null}
							</VStack>
						) : (
							<ArchiveEmptyState view={activeView} hasSearch={hasSearch} />
						)}

						<Box w="full" h={`calc(${MOBILE_BOTTOM_BAR_CLEARANCE} + 32px)`} flexShrink={0} />
					</VStack>
				</PageContent>
			</Flex>

			<ComposerDock showSolidFloor={false} showBottomFade>
				<AssistantInput
					query={draftText}
					setQuery={setDraftText}
					onSubmit={handleAsk}
					onFilterChange={setSearch}
					placeholder={`Search ${VIEW_LABELS[activeView]} or ask Bundle…`}
					mobilePlaceholder="Search archive or ask…"
					leadingIcon={<FiSearch size={18} />}
					askLabel="Ask Bundle"
					askReadinessState={knowledgeBaseReadinessState}
					waitForAskIntent
					isLoadingData={false}
					isRunning={false}
					autoFocus={false}
					surfaceShadow={BOTTOM_BAR_STRONG_SHADOW}
					focusShadow={BOTTOM_BAR_STRONG_FOCUS_SHADOW}
				/>
			</ComposerDock>
		</Flex>
	);
};

export default Archive;
